import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Package, Truck, ShoppingBag } from "lucide-react";

function normalizeArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function getItemName(item) {
  return item?.title || item?.name || "Untitled merch";
}

function getItemImages(item) {
  const photos = normalizeArray(item?.photos);
  if (photos.length) return photos;
  return [
    item?.imageUrl ||
      item?.image ||
      "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=1200&q=80",
  ];
}

export default function MerchDetails({ merch = [], events = [] }) {
  const { merchId } = useParams();
  const item = merch.find((entry) => (entry._id || entry.id) === merchId);

  const [selectedImage, setSelectedImage] = useState(0);
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [selectedVariant, setSelectedVariant] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] = useState("shipping");
  const [shippingAddress, setShippingAddress] = useState({
    fullName: "",
    line1: "",
    line2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "Canada",
  });

  const images = useMemo(() => getItemImages(item), [item]);
  const variants = useMemo(
    () => normalizeArray(item?.variants || item?.sizes),
    [item]
  );
  const deliveryMethods = useMemo(() => {
    const methods = normalizeArray(item?.deliveryMethods);
    return methods.length ? methods : ["shipping"];
  }, [item]);

  const linkedEvents = useMemo(() => {
    const ids = [
      ...normalizeArray(item?.associatedEvents),
      ...(item?.eventId ? [item.eventId] : []),
    ];
    return events.filter((event) => ids.includes(event._id || event.id));
  }, [item, events]);

  const parsedQuantity = Math.max(parseInt(quantity, 10) || 1, 1);
  const unitPrice = Number(item?.price) || 0;
  const totalPrice = unitPrice * parsedQuantity;

  const updateShippingField = (field, value) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleCheckout = async () => {
    if (!item) return;

    if (!buyerName || !buyerEmail) {
      alert("Please enter your name and email.");
      return;
    }

    if (fulfillmentMethod === "shipping") {
      const requiredFields = [
        shippingAddress.fullName,
        shippingAddress.line1,
        shippingAddress.city,
        shippingAddress.province,
        shippingAddress.postalCode,
        shippingAddress.country,
      ];

      if (requiredFields.some((field) => !field)) {
        alert("Please complete the shipping address before continuing.");
        return;
      }
    }

    try {
      const res = await fetch("/api/create-merch-checkout-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          merchItemId: item._id || item.id,
          buyerName,
          buyerEmail,
          quantity: parsedQuantity,
          selectedVariant,
          fulfillmentMethod,
          shippingAddress:
            fulfillmentMethod === "shipping" ? shippingAddress : null,
          associatedEventId: linkedEvents[0]?._id || linkedEvents[0]?.id || "",
        }),
      });

      const data = await res.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.message || "Failed to start merch checkout.");
      }
    } catch (error) {
      console.error(error);
      alert("Error starting merch checkout.");
    }
  };

  if (!item) {
    return (
      <main className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Merch item not found
          </h1>
          <p className="mt-2 text-sm text-slate-500">
            This item may have been removed or is no longer available.
          </p>
          <Link
            to="/merch"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to merch
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Link
          to="/merch"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to merch
        </Link>

        <div className="mt-8 grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <section className="space-y-6">
            <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="grid gap-0 lg:grid-cols-[1fr_120px]">
                <div className="relative h-[420px] bg-slate-100">
                  <img
                    src={images[selectedImage]}
                    alt={getItemName(item)}
                    className="h-full w-full object-cover"
                  />
                </div>

                <div className="flex gap-3 overflow-x-auto p-4 lg:flex-col lg:overflow-y-auto">
                  {images.map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => setSelectedImage(index)}
                      className={`overflow-hidden rounded-2xl border transition ${
                        selectedImage === index
                          ? "border-slate-900 ring-2 ring-slate-200"
                          : "border-slate-200"
                      }`}
                    >
                      <img
                        src={image}
                        alt={`${getItemName(item)} ${index + 1}`}
                        className="h-24 w-24 object-cover lg:h-28 lg:w-full"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Merch
                </span>
                {linkedEvents.length > 0 && (
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-700">
                    Linked to {linkedEvents.length} event{linkedEvents.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>

              <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                {getItemName(item)}
              </h1>
              <p className="mt-2 text-sm text-slate-500">
                {item.sellerName || item.bandName || item.artistName || "Independent seller"}
              </p>

              <p className="mt-6 text-base leading-7 text-slate-600">
                {item.description || "Merchandise sold through OnStage."}
              </p>

              {linkedEvents.length > 0 && (
                <div className="mt-8 rounded-[1.5rem] bg-slate-50 p-5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <CalendarDays className="h-4 w-4 text-slate-400" />
                    Available with these events
                  </div>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {linkedEvents.map((event) => (
                      <Link
                        key={event._id || event.id}
                        to={`/events/${event._id || event.id}`}
                        className="rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100"
                      >
                        {event.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          <aside className="space-y-6 lg:sticky lg:top-8">
            <div className="rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 bg-slate-50/80 p-6">
                <p className="text-sm font-medium text-slate-500">Purchase</p>
                <div className="mt-2 flex items-end gap-2">
                  <span className="text-4xl font-semibold tracking-tight text-slate-900">
                    ${unitPrice.toFixed(2)}
                  </span>
                  <span className="pb-1 text-sm text-slate-500">each</span>
                </div>
              </div>

              <div className="space-y-5 p-6">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Full name
                  </label>
                  <input
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="Enter your name"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    placeholder="Enter your email"
                  />
                </div>

                {variants.length > 0 && (
                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">
                      Variant
                    </label>
                    <select
                      value={selectedVariant}
                      onChange={(e) => setSelectedVariant(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                    >
                      <option value="">Select an option</option>
                      {variants.map((variant) => (
                        <option key={variant} value={variant}>
                          {variant}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Quantity
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Fulfillment
                  </label>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {deliveryMethods.map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => setFulfillmentMethod(method)}
                        className={`rounded-2xl border px-4 py-3 text-sm font-medium transition ${
                          fulfillmentMethod === method
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        {method === "shipping" ? "Ship to me" : "Pickup / handoff"}
                      </button>
                    ))}
                  </div>
                </div>

                {fulfillmentMethod === "shipping" && (
                  <div className="space-y-3 rounded-[1.5rem] bg-slate-50 p-4">
                    <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                      <Truck className="h-4 w-4 text-slate-400" />
                      Shipping address
                    </div>
                    <input
                      value={shippingAddress.fullName}
                      onChange={(e) => updateShippingField("fullName", e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      placeholder="Recipient name"
                    />
                    <input
                      value={shippingAddress.line1}
                      onChange={(e) => updateShippingField("line1", e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      placeholder="Address line 1"
                    />
                    <input
                      value={shippingAddress.line2}
                      onChange={(e) => updateShippingField("line2", e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                      placeholder="Address line 2 (optional)"
                    />
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        value={shippingAddress.city}
                        onChange={(e) => updateShippingField("city", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="City"
                      />
                      <input
                        value={shippingAddress.province}
                        onChange={(e) => updateShippingField("province", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="Province / State"
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <input
                        value={shippingAddress.postalCode}
                        onChange={(e) => updateShippingField("postalCode", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="Postal / ZIP code"
                      />
                      <input
                        value={shippingAddress.country}
                        onChange={(e) => updateShippingField("country", e.target.value)}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none transition focus:border-slate-400 focus:ring-4 focus:ring-slate-100"
                        placeholder="Country"
                      />
                    </div>
                  </div>
                )}

                <div className="rounded-[1.5rem] bg-slate-50 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                    <Package className="h-4 w-4 text-slate-400" />
                    Order summary
                  </div>
                  <div className="mt-4 space-y-2 text-sm text-slate-600">
                    <div className="flex items-center justify-between">
                      <span>Item</span>
                      <span>{getItemName(item)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Quantity</span>
                      <span>{parsedQuantity}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Fulfillment</span>
                      <span>{fulfillmentMethod === "shipping" ? "Shipping" : "Pickup"}</span>
                    </div>
                    <div className="flex items-center justify-between border-t border-slate-200 pt-3 text-base font-semibold text-slate-900">
                      <span>Total</span>
                      <span>${totalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCheckout}
                  className="w-full rounded-2xl bg-emerald-600 px-5 py-4 text-sm font-semibold text-white transition hover:bg-emerald-700"
                >
                  Continue to payment
                </button>

                <p className="text-center text-xs leading-5 text-slate-500">
                  Sellers manage delivery and fulfillment. Shipping orders should include tracking once dispatched.
                </p>
              </div>
            </div>

            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <ShoppingBag className="h-4 w-4 text-slate-400" />
                Seller fulfillment
              </div>
              <p className="mt-3 text-sm leading-6 text-slate-600">
                This item is fulfilled directly by the seller. Pickup orders can be handed off in person, and shipped orders should include tracking.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
